import { useEffect, useState, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { mediaUrl } from '../../utils/media';
import { XMarkIcon, TagIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const POLL_INTERVAL = 8000;

function formatTimer(ms) {
  if (ms <= 0) return '00:00';
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function PaymentModal({ order, ebook, user, buying, onConfirm, onClose, onSuccess }) {
  const hasOrder = !!order?.id;
  const [status, setStatus] = useState(order?.status || 'pending');
  const [qrData, setQrData] = useState(null);
  const [timeLeft, setTimeLeft] = useState(300000);
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherResult, setVoucherResult] = useState(null);
  const [voucherError, setVoucherError] = useState('');
  const [applyingVoucher, setApplyingVoucher] = useState(false);
  const [appliedVoucher, setAppliedVoucher] = useState(null);

  const title = ebook?.title || order?.ebook_title || 'Ebook';
  // order.amount wins: cart (multi-ebook) dan order real sudah punya total final
  const basePrice = Number(order?.amount != null ? order.amount : (ebook?.final_price || ebook?.price || 0));

  // Price display
  const originalPrice = order?.original_amount || basePrice;
  const discountAmount = order?.discount_amount || appliedVoucher?.discount_amount || 0;
  const finalPrice = hasOrder && order?.amount != null ? order.amount : (originalPrice - discountAmount);

  // Apply voucher
  const handleApplyVoucher = async () => {
    if (!voucherCode.trim()) return;
    setApplyingVoucher(true);
    setVoucherError('');
    try {
      const res = await api.post('/api/vouchers/apply', {
        code: voucherCode.trim(),
        amount: originalPrice,
      });
      setAppliedVoucher(res.data);
      setVoucherResult(res.data);
    } catch (err) {
      setVoucherError(err.response?.data?.detail || 'Voucher tidak valid');
      setAppliedVoucher(null);
      setVoucherResult(null);
    } finally {
      setApplyingVoucher(false);
    }
  };

  // Remove voucher
  const removeVoucher = () => {
    setVoucherCode('');
    setAppliedVoucher(null);
    setVoucherResult(null);
    setVoucherError('');
  };

  // Pass voucher to parent on confirm
  const handleConfirm = () => {
    onConfirm?.(voucherCode.trim() || undefined);
  };

  // Fetch QR code data from backend (fase 2)
  useEffect(() => {
    if (!hasOrder) { setQrData(null); return; }
    api.get(`/api/orders/${order.id}/qrcode`).then(res => {
      setQrData(res.data);
      if (res.data.expires_at) {
        const ms = new Date(res.data.expires_at).getTime() - Date.now();
        setTimeLeft(ms > 0 ? ms : 0);
      }
    }).catch(() => {});
  }, [hasOrder, order?.id]);

  // Timer countdown
  useEffect(() => {
    if (timeLeft <= 0) return;
    const t = setInterval(() => setTimeLeft(p => p - 1000), 1000);
    return () => clearInterval(t);
  }, [timeLeft]);

  // Poll payment status (fase 2)
  const pollStatus = useCallback(async () => {
    if (status === 'paid' || !hasOrder) return;
    try {
      const res = await api.post(`/api/orders/${order.id}/check-qr`);
      const s = res.data.status;
      setStatus(s);
      if (s === 'paid') onSuccess?.();
    } catch {
      // silent
    }
  }, [order?.id, status, hasOrder, onSuccess]);

  useEffect(() => {
    if (status === 'paid' || !hasOrder) return;
    const timer = setInterval(pollStatus, POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [pollStatus, status, hasOrder]);

  const statusLabel = {
    pending: 'Menunggu Pembayaran',
    paid: 'Pembayaran Berhasil ✓',
    failed: 'Pembayaran Gagal',
    expired: 'Pembayaran Kedaluwarsa',
  };

  const statusColor = {
    pending: 'text-yellow-600 bg-yellow-50',
    paid: 'text-green-700 bg-green-50',
    failed: 'text-red-600 bg-red-50',
    expired: 'text-gray-500 bg-gray-100',
  };

  const isExpired = timeLeft <= 0 && status === 'pending';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
        {/* Header */}
        <div className="bg-wa-dark rounded-t-xl px-4 py-3 flex items-center justify-between">
          <div>
            <span className="text-white font-semibold">{hasOrder ? 'Pembayaran QRIS' : 'Konfirmasi Pembelian'}</span>
            <span className="text-green-200 text-xs flex items-center gap-1">🔒 Aman & Terenkripsi</span>
          </div>
          <button onClick={onClose} className="text-green-200 hover:text-white">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {/* Ebook info */}
          <div className="flex gap-3">
            {ebook?.cover_url && (
              <img src={mediaUrl(ebook.cover_url)} alt={title} className="w-16 h-16 rounded object-cover" onError={e => { e.target.style.display = 'none'; }} />
            )}
            <div className="min-w-0">
              <p className="font-semibold text-gray-800 text-sm line-clamp-2">{title}</p>
              {user?.name && <p className="text-xs text-gray-500 mt-0.5">Nama di sertifikat: <strong>{user.name}</strong></p>}
            </div>
          </div>

          {/* Voucher input — only in phase 1 */}
          {!hasOrder && (
            <div className="border border-dashed border-gray-300 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <TagIcon className="w-4 h-4 text-gray-400" />
                <span className="text-xs font-medium text-gray-600">Punya kode voucher?</span>
              </div>
              {appliedVoucher ? (
                <div className="flex items-center justify-between bg-green-50 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-semibold text-green-700">{appliedVoucher.code}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-green-600">-{appliedVoucher.discount_amount > 0 ? `Rp ${Number(appliedVoucher.discount_amount).toLocaleString('id-ID')}` : `${appliedVoucher.value}%`}</span>
                    <button onClick={removeVoucher} className="text-gray-400 hover:text-red-500 text-xs">✕</button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    value={voucherCode}
                    onChange={e => setVoucherCode(e.target.value.toUpperCase())}
                    placeholder="Masukkan kode"
                    className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-1.5 outline-none focus:border-wa-dark"
                    onKeyDown={e => e.key === 'Enter' && handleApplyVoucher()}
                  />
                  <button
                    onClick={handleApplyVoucher}
                    disabled={applyingVoucher || !voucherCode.trim()}
                    className="text-sm bg-wa-dark text-white px-3 py-1.5 rounded-lg font-medium disabled:opacity-50 transition"
                  >
                    {applyingVoucher ? '...' : 'Pakai'}
                  </button>
                </div>
              )}
              {voucherError && <p className="text-xs text-red-500 mt-1">{voucherError}</p>}
            </div>
          )}

          {/* Price summary */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-1">
            {discountAmount > 0 ? (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Harga ebook</span>
                  <span className="text-gray-500 line-through">Rp {originalPrice.toLocaleString('id-ID')}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-green-600">Diskon voucher ({appliedVoucher?.code || order?.voucher_code})</span>
                  <span className="text-green-600 font-medium">-Rp {discountAmount.toLocaleString('id-ID')}</span>
                </div>
                <div className="border-t border-gray-200 pt-1 flex justify-between">
                  <span className="text-sm font-semibold text-gray-800">Total</span>
                  <span className="text-wa-dark font-bold">Rp {finalPrice.toLocaleString('id-ID')}</span>
                </div>
              </>
            ) : (
              <div className="flex justify-between">
                <span className="text-sm font-semibold text-gray-800">Total</span>
                <span className="text-wa-dark font-bold text-lg">Rp {originalPrice.toLocaleString('id-ID')}</span>
              </div>
            )}
          </div>

          {/* Fase 1: konfirmasi */}
          {!hasOrder && (
            <>
              <div className="bg-green-50 border border-green-200 rounded-lg p-2 flex items-center gap-2">
                <span className="text-green-600 text-lg">🔒</span>
                <span className="text-xs text-green-700 font-medium">Pembayaran aman via QRIS</span>
              </div>
              <div className="flex gap-2">
                <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-600 text-sm py-2 rounded-lg">Batal</button>
                <button onClick={handleConfirm} disabled={buying} className="flex-1 bg-wa-dark text-white text-sm py-2 rounded-lg font-medium disabled:opacity-60">
                  {buying ? 'Memproses...' : 'Bayar via QRIS'}
                </button>
              </div>
            </>
          )}

          {/* Fase 2: status */}
          {hasOrder && (
            <>
              <div className={`text-center text-sm font-semibold py-2 rounded-lg ${statusColor[status] || statusColor.pending}`}>
                {statusLabel[status] || status}
              </div>

              {/* Order ID */}
              <div className="text-sm text-gray-600">
                <div className="flex justify-between py-1 border-b">
                  <span>Order ID</span>
                  <span className="font-mono text-xs">{order?.id}</span>
                </div>
              </div>
            </>
          )}

          {/* QRIS */}
          {hasOrder && status === 'pending' && qrData?.qris_code && !isExpired && (
            <div className="flex flex-col items-center gap-3">
              <div id="qris-svg-wrapper" className="bg-white p-4 rounded-xl border-2 border-gray-200 shadow-sm">
                <QRCodeSVG value={qrData.qris_code} size={200} level="M" includeMargin={false} imageSettings={{ src: '', height: 0, width: 0 }} />
              </div>
              <p className="text-xs text-gray-500">Scan QRIS ini dengan DANA / Mobile Banking</p>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-500">Batas waktu:</span>
                <span className={`font-mono font-bold ${timeLeft < 60000 ? 'text-red-500' : 'text-yellow-600'}`}>{formatTimer(timeLeft)}</span>
              </div>
              <button
                onClick={() => {
                  const svg = document.querySelector('#qris-svg-wrapper svg');
                  if (!svg) return;
                  const xml = new XMLSerializer().serializeToString(svg);
                  const img = new Image();
                  img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = 400; canvas.height = 400;
                    const ctx = canvas.getContext('2d');
                    ctx.fillStyle = '#ffffff';
                    ctx.fillRect(0, 0, 400, 400);
                    ctx.drawImage(img, 0, 0, 400, 400);
                    const a = document.createElement('a');
                    a.download = 'qris-pembayaran.png';
                    a.href = canvas.toDataURL('image/png');
                    a.click();
                  };
                  img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(xml)));
                }}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 border border-blue-300 rounded-lg px-3 py-1.5 hover:bg-blue-50 transition"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                Download QR
              </button>
              <p className="text-xs text-gray-400 text-center">Halaman otomatis update saat pembayaran berhasil</p>
              <button
                onClick={async () => {
                  try {
                    await api.post(`/api/orders/${order.id}/claim-paid`);
                    toast.success('Klaim dikirim, tunggu konfirmasi admin');
                    onClose?.();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Gagal kirim klaim');
                  }
                }}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-2 rounded-lg transition text-sm"
              >
                ✅ Saya Sudah Bayar
              </button>
            </div>
          )}

          {/* Loading QR */}
          {hasOrder && status === 'pending' && !qrData?.qris_code && !isExpired && (
            <div className="flex flex-col items-center gap-3 py-4">
              <div className="w-44 h-44 bg-gray-100 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center">
                <span className="text-5xl">📱</span>
              </div>
              <p className="text-xs text-gray-400">Membuat QRIS...</p>
            </div>
          )}

          {/* Expired */}
          {hasOrder && status === 'pending' && isExpired && (
            <div className="text-center py-4">
              <p className="text-red-500 font-medium">QRIS sudah kedaluwarsa</p>
              <p className="text-xs text-gray-400 mt-1">Silakan buat order baru</p>
            </div>
          )}

          {/* Paid */}
          {hasOrder && status === 'paid' && (
            <div className="text-center">
              <span className="text-5xl">🎉</span>
              <p className="text-green-700 font-semibold mt-2">Terima kasih! Ebook sudah tersedia di Perpustakaan.</p>
            </div>
          )}

          {/* Dev simulate */}
          {process.env.NODE_ENV !== 'production' && hasOrder && status !== 'paid' && (
            <button
              onClick={async () => {
                try {
                  await api.post(`/api/payment/simulate-paid/${order.id}`);
                  const res = await api.get(`/api/orders/${order.id}`);
                  const s = res.data.status;
                  setStatus(s);
                  if (s === 'paid') onSuccess?.();
                } catch(e) { console.error(e); }
              }}
              className="w-full border border-dashed border-gray-300 text-gray-400 hover:text-wa-dark hover:border-wa-dark text-xs py-1.5 rounded-lg transition"
            >
              🧪 Simulasi Bayar (Dev)
            </button>
          )}

          {hasOrder && (
            <button onClick={onClose} className="w-full border border-gray-300 text-gray-600 text-sm py-2 rounded-md hover:bg-gray-50 transition">Tutup</button>
          )}
        </div>
      </div>
    </div>
  );
}
